import React, { useEffect, useMemo, useCallback } from "react";
import { Typography, Grid, Icon, Button, styled } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
// Components
import { SelectField } from "src/components/FormikComponents/SelectField";
import { TextField } from "src/components/FormikComponents/TextField";

// Redux
import { TimelineEvent } from "src/redux/reducers/FlightTimelineReducer";
import { searchFlights, searchGroundEvents } from "src/redux/actions/flights/FlightData.Actions";
import {
  IUser,
  IFlightAircraft,
  ICustomer,
  IFlightType,
  IDynamicQuery,
  IAircraft,
  IAircraftType,
  IFlightFlag,
  FlightRemovalType,
} from "@vmosolutions/oc-interfaces";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { useSelector, useDispatch } from "react-redux";
import _ from "lodash";

// Airport Retrieval
import { airportSearchString, updateAirportOptions } from "src/utils/airportHelper";
import { IStructuresState, IAirportOption } from "src/redux/reducers/StructuresReducer";

// API Calls
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { storeAppUsers } from "src/redux/actions/Structure.Actions";

// Styling
import classNames from "classnames";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Tooltip from "@mui/material/Tooltip";

import {
  defaultSearchStatus,
  FilterWithDateInput,
  IEventFilter,
  returnSelectedOptions,
  sortUserOptions,
} from "./Helpers";
import {
  convertFilter2Query,
  flightFilterOptions,
  eventFilterOptions,
  basicSearchFilters,
  validationStatusOf,
} from "./EventHelper";

import { IFilterOption } from "@vmosolutions/oc-interfaces";

const OCEventFilter = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  "&.saved_search_selection": {
    marginLeft: 10,
    width: "auto",
    "& .saved_search_icon": {
      position: "relative",
      width: 24,
      height: 24,
      "& .search_icon": {},
      "& .star_icon": {
        width: 9,
        height: 10,
        top: 4,
        left: 5,
        position: "absolute",
      },
    },
    "& .timeline_event_saved_search_filter": {
      width: 263,
    },
  },
  "& .filter_drop_down_menu": {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    margin: "5px 10px",
    alignItems: "flex-start",
    "& .timeline_event_search_filter": {
      width: 263,
    },
  },
  "& .active_filters": {
    "& .filter": { width: 263 },
    display: "flex",
    marginLeft: 10,
    alignItems: "flex-start",
    flexWrap: "wrap",
    "& .date_range_filter": {
      display: "flex",
      width: 263,
      flexDirection: "column",
      margin: 5,
      "& .from_date, & .to_date": {
        margin: 0,
        "& label": { width: 68 },
      },
      "& .daysofweek_selection": {
        margin: 5,
        "& .input_control label": { width: 68, margin: "0px 10px auto auto" },
        "& .react-select-container": { width: 175 },
      },
    },
    "& button.search_button": {
      margin: 3,
      padding: "2px 8px",
      fontSize: 12,
      fontWeight: 300,
      width: 100,
      "& .search_icon": { fontSize: 18, marginRight: 5 },
    },
    "& .results_match_filters": {
      margin: 3,
      borderRadius: 3,
      width: 26,
      height: 26,
      border: `1px solid ${theme.palette.success.main}`,
      "& path": { fill: theme.palette.success.main },
    },
  },
}));

/**
 * EventFilter component handles the ability to search for a flight and the results will show on the timeline
 * @param props -
 */
export const EventFilter = (props: IEventFilter): JSX.Element => {
  const dispatch: any = useDispatch();
  const {
    dataOutput,
    parentElement,
    hideLabel,
    allowMultiFilter,
    searchStatus,
    showDateRangeRRule,
    inHeader,
    setSearchFilters,
    applySavedFilter,
  } = props;
  const structures: IStructuresState = useSelector((state: IRootState) => state.structures);
  const { airportOptions, customerStructure } = structures;
  const { aircraft, flightTypes, customers, flightFlags } = customerStructure;

  // Load aircraftTypes (used in "aircraftType" filter)
  const [aircraftTypes, setAircraftTypes] = React.useState<IAircraftType[]>([]);

  useEffect(() => {
    const getTypes = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.Get<IAircraftType[]>("/aircraftTypes");
      if (Array.isArray(response)) setAircraftTypes(response);
    };
    getTypes();
  }, []);

  // Load appUsers from redux, fetch them if not available.
  const currentUser: string | undefined = useSelector((state: IRootState) => state?.authentication?.attributes?._id);
  const appUsers = useSelector((state: IRootState) => state.structures.appUsers);

  useEffect(() => {
    const getUsersAction = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.GetPaginate<IUser>("/users");
      if (response) dispatch(storeAppUsers(response));
    };
    if (appUsers?.length === 0) getUsersAction();
  }, [dispatch, appUsers]);

  // Store Events loaded by Database Search
  // const [databaseResults, setDatabaseResults] = React.useState<TimelineEvent[]>([]);
  const [searching, setSearching] = React.useState<boolean>(false);

  // Store filtered events
  const [filteredEvents, setFilteredEvents] = React.useState<TimelineEvent[]>([]);
  // const [aircraftOptions, setAircraftOptions] = React.useState<IFlightAircraft[]>([]);

  // Store search and selected filters, compared to verify results match current filters.
  const [searchFilter, setSearchFilter] = React.useState<IFilterOption[]>([]);
  const [selectedFilter, setSelectedFilter] = React.useState<IFilterOption[]>([]);
  // const [savedSearchFilter, setSavedSearchFilter] = React.useState<ISavedFilter>(null);

  const filterType = selectedFilter?.length > 0 ? selectedFilter[0]?.type : undefined;
  const dataType = useMemo(() => props?.dataType ?? filterType, [props?.dataType, filterType]);

  // If a selected search filter is passed in from saved filters, apply it. - CC
  useEffect(() => {
    if (applySavedFilter?.dataType === dataType) {
      setSelectedFilter(applySavedFilter?.filters ?? []);
    }
  }, [applySavedFilter, dataType]);

  // If there are valid filters present, update the upstream search filters, otherwise, clear them
  useEffect(() => {
    if (setSearchFilters) setSearchFilters(selectedFilter ?? []);
  }, [selectedFilter, setSearchFilters]);

  // Return true if the event is of type flight. (basic search uses filterType, dataType is used in advanced search)
  const isFlight = useMemo(() => {
    return dataType === "flights" ? true : false;
  }, [dataType]);

  const resultsMatchFilter = useMemo(() => {
    return _.isEqual(searchFilter, selectedFilter);
  }, [searchFilter, selectedFilter]);

  // If dataType is passed through props, use advanced filter options, otherwise use basic.
  const filterOptions = useMemo(() => {
    switch (props?.dataType) {
      case "flights":
        return { flightEvents: flightFilterOptions };
      case "groundEvents":
        return { groundEvents: eventFilterOptions };
      default:
        return basicSearchFilters;
    }
  }, [props?.dataType]);

  // Make sure all filters contain a value before enabling the search button.
  const enableSearch = useMemo(() => {
    let searchValid = true;
    selectedFilter?.forEach((filter) => {
      if (!filter.searchValue || (Array.isArray(filter.searchValue) && filter.searchValue?.length === 0)) {
        searchValid = false; // set search as invalid if any filters have no assigned value.
      }
    });
    return searchValid;
  }, [selectedFilter]);

  // Clear all filters and results. (resets the tool)
  const handleClearAll = useCallback(() => {
    setSearchFilter([]);
    setSelectedFilter([]);
    setFilteredEvents([]);
    // setSavedSearchFilter(null);
  }, []);

  // If dataType passed in through props changes , reset tool.
  useEffect(() => {
    handleClearAll();
  }, [props?.dataType, handleClearAll]);

  // Debounced search flights call.
  // This reduces the possibility of making duplicate APIs calls in quick succession.
  const debouncedSearchEvents = _.debounce(async (filters: any): Promise<void> => {
    const searchPayload: IDynamicQuery = convertFilter2Query(filters, isFlight);
    // console.log("IDynamicQuery", searchPayload);
    // Only make the api call if there are filters showing in the query.

    if (searchPayload?.queryArgs?.length > 0) {
      searchStatus({
        active: true,
        message: `Searching for requested ${dataType}...`,
      });
      setSearching(true);
      const searchResults: TimelineEvent[] = isFlight
        ? await dispatch(searchFlights(searchPayload))
        : await dispatch(searchGroundEvents(searchPayload));

      // If response isn't an array, empty table so that search status can be shown.
      let newResults = Array.isArray(searchResults) ? searchResults : [];

      const customerFilter = _.find(filters, (f: IFilterOption) => f.value.includes("customer"));
      if (customerFilter?.searchValue?.includes("unassigned")) {
        const realCustomerIds = _.without(customerFilter.searchValue, "unassigned");
        newResults = newResults.filter(
          (event: any) =>
            _.isEmpty(event.customer) || _.some(event.customer, (c: ICustomer) => realCustomerIds.includes(c._id))
        );
      }

      setFilteredEvents(newResults);
      // Store current selected filter (clone to ensure no mutation) as search filter.
      setSearchFilter(_.cloneDeep(filters));
      setSearching(false);

      if (Array.isArray(searchResults)) {
        if (searchResults.length > 0) {
          const status = { ...defaultSearchStatus, lastSearched: new Date() };
          searchStatus(status);
        } else {
          searchStatus({
            active: false,
            message: `No ${dataType} found, please change filter and try again.`,
            lastSearched: new Date(),
          });
        }
      } else {
        const error: { status?: number; statusText?: string } = searchResults as any;
        //console.log("search error:", error);
        switch (error?.status) {
          case 413: // Response exceeds allowable size
            searchStatus({
              active: false,
              code: 413,
              message: "The response size is too large. Please refine your search parameters and try again.",
              lastSearched: new Date(),
            });
            break;
          default:
            searchStatus({
              active: false,
              message: "Something went wrong please try again or contact administrator for assistance.",
              lastSearched: new Date(),
            });
          // code block
        }
      }
    }
  }, 1000);

  // If filtered events change, update parent.
  useEffect(() => {
    if (dataOutput) dataOutput(filteredEvents);
  }, [filteredEvents, dataOutput]);

  const selectionGroupedOptions = useMemo(() => {
    const groupArray: any[] = [];
    if (!filterOptions) {
      return groupArray;
    }
    // Flights
    if (filterOptions.hasOwnProperty("flightEvents")) {
      groupArray.push({
        label: "Flight Event Fields",
        options: filterOptions.flightEvents,
      });
    }
    // Ground Events
    if (filterOptions.hasOwnProperty("groundEvents")) {
      groupArray.push({
        label: "Ground Event Fields",
        options: filterOptions.groundEvents,
      });
    }
    return groupArray;
  }, [filterOptions]);

  //add an unassigned aircraft option
  const aircraftOptions = useMemo(() => {
    const unassignedOption: IAircraft[] = [
      {
        _id: "unassigned",
        registration: "Unassigned",
        aircraftType: { title: "", codeIATA: "", codeICAO: "" },
        generateErrors: false,
        subCharter: false,
        planning: false,
        isActive: false,
        requireMessages: false,
        serialNumber: "",
        foreignRef: [],
        state: undefined,
        custom: undefined,
      },
    ];
    if (aircraft) {
      return [...unassignedOption, ...aircraft];
    }
    return unassignedOption;
  }, [aircraft]);

  const customerOptions = useMemo(() => {
    const noCustomerOption: ICustomer[] = [
      {
        _id: "unassigned",
        name: "No customer assigned",
        details: "",
        active: false,
        customerType: undefined as any,
        custom: {},
      },
    ];
    if (customers) {
      return [...noCustomerOption, ...customers];
    }
    return noCustomerOption;
  }, [customers]);

  const validationOptions = useMemo(
    //I didn't use true, false and null for the values
    //because there was a console warnign when I had null as the value field
    () => [
      { label: "Validated - No Errors", value: 1, booleanValue: true },
      { label: "Validated - Errors", value: 2, booleanValue: false },
      { label: "Unvalidated", value: 3, booleanValue: null },
    ],
    []
  );

  /**
   * Accepts the user's input & determines the type of the user's input.
   * @param event -
   */
  const handleSearch = useCallback(
    (key: string, event: any): void => {
      const newFilter = [...selectedFilter];
      const updateFilter = newFilter.find((filter: IFilterOption) => filter.value.includes(key));
      const searchValue = !!event && !!event.target ? event.target.value : event;
      if (updateFilter) {
        switch (key) {
          case "scheduledOff":
          case "scheduledOn":
          case "scheduledOut":
          case "scheduledIn":
          case "actualOff":
          case "actualOn":
          case "actualOut":
          case "actualIn":
          case "scheduledStart":
          case "scheduledEnd":
          case "actualStart":
          case "actualEnd":
          case "createdAt":
          case "modifiedAt":
            updateFilter.searchValue = event;
            break;
          // All fields which use _id to define selected values.
          case "aircraft":
          case "customer":
          case "flightType":
          case "eventType":
          case "createdBy":
          case "modifiedBy":
            updateFilter.searchValue = searchValue?.map((option: any) => option._id);
            break;
          // Fields that aren't working properly with _id... use title
          //TODO Look into fixing these so they can use _id
          case "flags":
          case "aircraftType":
            updateFilter.searchValue = searchValue?.map((option: any) => option.title);
            break;
          case "flightNumber": {
            const numberArray = searchValue
              ? searchValue.split(",").map((flightNumber: string) => flightNumber.trim())
              : null;
            updateFilter.searchValue = numberArray;
            break;
          }
          case "origin":
          case "destination":
          case "location":
          case "alternate":
            // Map location by using their airport ident (string)
            updateFilter.searchValue = searchValue?.map((option: any) => option.ident);
            break;
          case "batchId": {
            const batchIdArray = searchValue
              ? searchValue?.split(",").map((bactchId: string) => bactchId.trim())
              : null;
            updateFilter.searchValue = batchIdArray;
            break;
          }
          case "departureGate":
          case "arrivalGate": {
            const gateArray = searchValue
              ? searchValue?.split(",").map((gate: string) => gate.trim())
              : null;
            updateFilter.searchValue = gateArray;
            break;
          }
          case "validation":
            // single select: react-select hands back one option, or null when cleared
            updateFilter.searchValue = searchValue?.label ?? null;
            break;
          case "title":
            updateFilter.searchValue = searchValue.trim();
            break;
          default:
            updateFilter.searchValue = searchValue;
        }
        searchStatus(defaultSearchStatus);
        setSelectedFilter(newFilter);
      }
    },
    [searchStatus, selectedFilter]
  );

  /**
   * Handles the user's selected filter to search for a flight.
   * @param selectedFilter -
   */
  const handleSelectedFilter = useCallback(
    (newFilters: any): void => {
      const filterArray = !newFilters
        ? []
        : Array.isArray(newFilters)
          ? _.cloneDeep(newFilters)
          : [_.cloneDeep(newFilters)];
      // Carry over any currently selected filter values (required for using multiple filters)
      if (filterArray.length > 0) {
        filterArray?.map((newF: IFilterOption) => {
          const filter = selectedFilter.find((oldF: IFilterOption) => oldF.value === newF.value);
          if (!!filter && !!filter.searchValue) {
            newF.searchValue = filter.searchValue;
          }
          return false;
        });
      }
      searchStatus(defaultSearchStatus);
      setSelectedFilter(filterArray);
    },
    [searchStatus, selectedFilter]
  );

  const filterValues = (options: IAircraftType[]) => {
    return options.filter((option, index, arr) => {
      return arr.findIndex((item) => item.title === option.title) === index;
    });
  };

  /**
   * Maps out selected filter array and returns the selected field inputs if selected.
   */
  const renderFilterInputs = useCallback((): JSX.Element => {
    if (!selectedFilter || selectedFilter.length === 0) {
      return null;
    }

    return (
      <>
        {selectedFilter?.map((filter: IFilterOption, index: number) => {
          const { label, value, type } = filter;

          const airportTypes = ["origin", "destination", "location", "alternate"];
          const dateTypes = [
            "scheduledOff",
            "scheduledOn",
            "scheduledStart",
            "scheduledEnd",
            "actualStart",
            "actualEnd",
            "actualIn",
            "actualOut",
            "actualOn",
            "actualOff",
            "scheduledIn",
            "scheduledOut",
            "createdAt",
            "modifiedAt",
          ];
          const key = type === "flights" ? value.replace("f_", "") : value.replace("g_", "");

          // Date Fields
          if (dateTypes.includes(key)) {
            return (
              <div key={index}>
                <FilterWithDateInput
                  className="auto_width filter date_range_filter"
                  dateTimeRange={filter.searchValue}
                  input={{ type: "text" }}
                  name={label}
                  onChange={(event: any) => handleSearch(key, event)}
                  type="date"
                  showDateRangeRRule={showDateRangeRRule}
                />
              </div>
            );
          } else if (key === "flightNumber" || key === "title") {
            return (
              <div key={index}>
                <TextField
                  className="auto_width filter table_filter_textfield"
                  label={label}
                  name={key}
                  value={filter.searchValue}
                  onChange={(event: any) => handleSearch(key, event)}
                />
              </div>
            );
          } else if (airportTypes.includes(key)) {
            return (
              <div key={index}>
                <SelectField
                  className="auto_width filter airport_selection"
                  label={label}
                  name={key}
                  value={returnSelectedOptions(Object.values(airportOptions), filter.searchValue, "ident")}
                  isMulti={true}
                  onChange={(airports: IAirportOption[]) => handleSearch(key, airports)}
                  onInputChange={(ident: string) => {
                    if (ident?.length >= 2) {
                      // Only run a search if 2 or more characters exist in search string.
                      updateAirportOptions(ident, airportOptions, dispatch);
                    }
                  }}
                  options={Object.values(airportOptions)}
                  getOptionLabel={(airport: IAirportOption) =>
                    airport ? `${airport.ident}/${airport.iata} - ${airport.name}` : ""
                  }
                  getOptionValue={(airport: IAirportOption) => airport.ident}
                  noOptionsMessage={() => airportSearchString}
                  isSearchable={true}
                  isClearable={true}
                  menuPosition="fixed"
                  maxMenuHeight={400}
                  menuPortalTarget={parentElement}
                  openMenuOnFocus={false}
                  blurInputOnSelect={true}
                  closeMenuOnSelect={true}
                  placeholder=""
                />
              </div>
            );
          } else if (value.includes("customer")) {
            return (
              <div key={index}>
                <SelectField
                  className={
                    type === "flights"
                      ? "auto_width filter flight_customer_search"
                      : "auto_width filter event_customer_search"
                  }
                  label={label}
                  name={key}
                  value={returnSelectedOptions(customerOptions, filter.searchValue, "_id")}
                  isMulti={true}
                  onChange={(customer: ICustomer[]) => handleSearch(key, customer)}
                  options={customerOptions}
                  getOptionLabel={(customer: ICustomer) => (customer ? customer.name : "")}
                  getOptionValue={(customer: ICustomer) => customer._id}
                  noOptionsMessage={() => "no customers available"}
                  isSearchable={true}
                  isClearable={true}
                  menuPosition="fixed"
                  maxMenuHeight={400}
                  menuPortalTarget={parentElement}
                  openMenuOnFocus={false}
                  blurInputOnSelect={true}
                  closeMenuOnSelect={true}
                  placeholder=""
                />
              </div>
            );
          } else if (value.includes("eventType") || value.includes("flightType")) {
            const typeOptions =
              type === "groundEvents"
                ? flightTypes.filter((type: IFlightType) => !type.isFlight)
                : flightTypes.filter((type: IFlightType) => type.isFlight);
            const typeLabel = value.includes("eventType") ? "event" : "flight";

            return (
              <div key={index}>
                <SelectField
                  className="auto_width filter"
                  label={label}
                  name={key}
                  value={returnSelectedOptions(typeOptions, filter.searchValue, "_id")}
                  isMulti={true}
                  onChange={(types: IFlightType[]) => handleSearch(key, types)}
                  options={typeOptions.sort((a, b) => {
                    return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
                  })}
                  getOptionLabel={(option: any) => (option ? option.description : "")}
                  getOptionValue={(option: any) => option._id}
                  noOptionsMessage={() => `no ${typeLabel} types available`}
                  isSearchable={true}
                  isClearable={true}
                  menuPosition="fixed"
                  maxMenuHeight={400}
                  menuPortalTarget={parentElement}
                  openMenuOnFocus={false}
                  blurInputOnSelect={true}
                  closeMenuOnSelect={true}
                  placeholder=""
                />
              </div>
            );
          } else if (value.endsWith("aircraft")) {
            return (
              <div key={index}>
                <SelectField
                  className={
                    type === "flights"
                      ? "auto_width filter flight_aircraft_search"
                      : "auto_width filter event_aircraft_search"
                  }
                  label={label}
                  name={key}
                  value={returnSelectedOptions(aircraftOptions, filter.searchValue, "_id")}
                  isMulti={true}
                  onChange={(aircraft: IFlightAircraft[]) => handleSearch(key, aircraft)}
                  options={aircraftOptions}
                  getOptionLabel={(aircraft: IFlightAircraft) =>
                    aircraft
                      ? `${aircraft?.registration} ${
                          aircraft?.aircraftType?.title && "(" + aircraft.aircraftType.title + ")"
                        }`
                      : ""
                  }
                  getOptionValue={(aircraft: IFlightAircraft) => aircraft._id}
                  noOptionsMessage={() => "no aircraft available"}
                  isSearchable={true}
                  isClearable={true}
                  menuPosition="fixed"
                  maxMenuHeight={400}
                  menuPortalTarget={parentElement}
                  openMenuOnFocus={false}
                  blurInputOnSelect={true}
                  closeMenuOnSelect={true}
                  placeholder=""
                />
              </div>
            );
          } else if (value.includes("aircraftType")) {
            return (
              <div key={index}>
                <SelectField
                  className={
                    type === "flights"
                      ? "auto_width filter flight_aircraftType_search"
                      : "auto_width filter event_aircraftType_search"
                  }
                  label={label}
                  name={key}
                  value={filterValues(returnSelectedOptions(aircraftTypes, filter.searchValue, "title"))}
                  isMulti={true}
                  onChange={(aircraftType: IAircraftType[]) => handleSearch(key, aircraftType)}
                  options={aircraftTypes}
                  getOptionLabel={(aircraftType: IAircraftType) => (aircraftType ? aircraftType.title : "")}
                  getOptionValue={(aircraftType: IAircraftType) => aircraftType.title}
                  noOptionsMessage={() => "no aircraft types available"}
                  isSearchable={true}
                  isClearable={true}
                  menuPosition="fixed"
                  maxMenuHeight={400}
                  menuPortalTarget={parentElement}
                  openMenuOnFocus={false}
                  blurInputOnSelect={true}
                  closeMenuOnSelect={true}
                  placeholder=""
                />
              </div>
            );
          } else if (value.includes("validation")) {
            return (
              <div key={index}>
                <SelectField
                  className="auto_width filter"
                  label={label}
                  name={key}
                  value={
                    validationOptions.find((option) => option.label === validationStatusOf(filter.searchValue)) ?? null
                  }
                  isMulti={false}
                  onChange={(validationOptionsSelections) => handleSearch(key, validationOptionsSelections)}
                  options={validationOptions}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  noOptionsMessage={() => "no validation status options available"}
                  isSearchable={true}
                  isClearable={true}
                  menuPosition="fixed"
                  maxMenuHeight={400}
                  menuPortalTarget={parentElement}
                  openMenuOnFocus={false}
                  blurInputOnSelect={true}
                  closeMenuOnSelect={true}
                  placeholder=""
                />
              </div>
            );
          } else if (value.includes("flags")) {
            return (
              <div key={index}>
                <SelectField
                  className={
                    type === "flights"
                      ? "auto_width filter flight_flags_search"
                      : "auto_width filter event_flags_search"
                  }
                  label={label}
                  name={key}
                  value={returnSelectedOptions(flightFlags, filter.searchValue, "title")}
                  isMulti={true}
                  onChange={(flags: IFlightFlag[]) => handleSearch(key, flags)}
                  options={flightFlags.sort((a, b) => {
                    return a.description.toLowerCase().localeCompare(b.description.toLowerCase());
                  })}
                  getOptionLabel={(flag: IFlightFlag) => flag?.description ?? ""}
                  getOptionValue={(flag: IFlightFlag) => flag._id}
                  noOptionsMessage={() => "no flags available"}
                  isSearchable={true}
                  isClearable={true}
                  menuPosition="fixed"
                  maxMenuHeight={400}
                  menuPortalTarget={parentElement}
                  openMenuOnFocus={false}
                  blurInputOnSelect={true}
                  closeMenuOnSelect={true}
                  placeholder=""
                />
              </div>
            );
          } else if (key === "batchId" || key === "departureGate" || key === "arrivalGate") {
            return (
              <div key={index}>
                <TextField
                  className="auto_width filter table_filter_textfield"
                  label={label}
                  name={key}
                  value={filter.searchValue}
                  onChange={(event: any) => handleSearch(key, event)}
                />
              </div>
            );
          } else if (key === "removalType") {
            return (
              <div key={index}>
                <SelectField
                  className="auto_width filter"
                  label={label}
                  name={key}
                  value={filter.searchValue}
                  isMulti={true}
                  onChange={(value: any) => handleSearch(key, value)}
                  options={[
                    { value: FlightRemovalType.DeleteFlight, label: "Deleted Flight" },
                    { value: FlightRemovalType.CancelFlight, label: "Canceled Flight" },
                  ]}
                  // getOptionLabel={(option: any) => (option ? option.description : "")}
                  // getOptionValue={(option: any) => option._id}
                  noOptionsMessage={() => "no event types available"}
                  isSearchable={true}
                  isClearable={true}
                  menuPosition="fixed"
                  maxMenuHeight={400}
                  menuPortalTarget={parentElement}
                  openMenuOnFocus={false}
                  blurInputOnSelect={true}
                  closeMenuOnSelect={true}
                  placeholder=""
                />
              </div>
            );
          } else if (key === "createdBy" || key === "modifiedBy") {
            return (
              <div key={index}>
                <SelectField
                  className={`auto_width filter ${key}_search`}
                  label={label}
                  name={key}
                  value={returnSelectedOptions(appUsers, filter.searchValue, "_id")}
                  isMulti={true}
                  onChange={(users: IUser[]) => handleSearch(key, users)}
                  options={sortUserOptions(appUsers, currentUser)} //appUsers.sort((a, b) => { return a.lastName.toLowerCase().localeCompare(b.lastName.toLowerCase());                  })
                  getOptionLabel={(user: IUser) => {
                    const isMe = currentUser === user._id;
                    return `${user.lastName}, ${user.firstName} ${user?.employeeId ? `(${user.employeeId})` : ""} ${
                      isMe ? "(Me)" : ""
                    }`;
                  }}
                  getOptionValue={(user: IUser) => user._id}
                  noOptionsMessage={() => "no users available"}
                  isSearchable={true}
                  isClearable={true}
                  menuPosition="fixed"
                  maxMenuHeight={400}
                  menuPortalTarget={parentElement}
                  openMenuOnFocus={false}
                  blurInputOnSelect={true}
                  closeMenuOnSelect={true}
                  placeholder={`Select ${key} user`}
                />
              </div>
            );
          }

          return null;
        })}
      </>
    );
  }, [
    aircraftOptions,
    aircraftTypes,
    airportOptions,
    appUsers,
    currentUser,
    customers,
    dispatch,
    flightFlags,
    flightTypes,
    handleSearch,
    parentElement,
    selectedFilter,
    showDateRangeRRule,
    validationOptions,
  ]);

  return (
    <OCEventFilter className={classNames("search_filters")} style={{ alignItems: inHeader ? "center" : "flex-start" }}>
      {!hideLabel && (
        <Typography variant="subtitle1" className="body_text">
          Find {!selectedFilter || selectedFilter.length === 0 ? "" : isFlight ? "Flight" : "Ground"} Events by:
        </Typography>
      )}

      <div className="auto_width filter_drop_down_menu">
        {hideLabel && <Icon className="control_icon">filter_list</Icon>}
        <SelectField
          className="timeline_event_search_filter"
          label=""
          name="advanced_search_filters"
          value={selectedFilter}
          getOptionValue={(filter: IFilterOption) => filter.value}
          options={selectionGroupedOptions}
          onChange={handleSelectedFilter}
          isMulti={allowMultiFilter}
          isClearable={true}
          menuPortalTarget={parentElement}
          menuPosition="fixed"
          maxMenuHeight={400}
          multiMode={allowMultiFilter ? "checkbox" : null}
          placeholder={
            selectedFilter?.length === 0
              ? "Select Filters..."
              : selectedFilter?.length === 1
                ? selectedFilter[0].label
                : "Multiple Filters Selected..."
          }
        />
      </div>

      {selectedFilter?.length > 0 && (
        <div className="active_filters">
          {renderFilterInputs()}
          <Grid container style={{ width: "auto" }}>
            <Button
              id={"search_btn"}
              variant="outlined"
              // color="secondary"
              onClick={() => handleClearAll()}
              disabled={filteredEvents?.length === 0 || selectedFilter?.length === 0}
              className="search_button"
            >
              Clear
            </Button>

            <Button
              id={"search_btn"}
              variant="outlined"
              onClick={() => debouncedSearchEvents(selectedFilter)}
              disabled={!enableSearch || searching}
              className="search_button"
            >
              {!searching ? (
                <>
                  <Icon className="search_icon">{resultsMatchFilter ? "refresh" : "search"}</Icon>
                  {resultsMatchFilter ? "Refresh" : "Search"}
                </>
              ) : (
                <>
                  <CircularProgress color="inherit" style={{ width: 14, height: 14, marginRight: 8 }} /> searching
                </>
              )}
            </Button>

            {resultsMatchFilter && (
              <Tooltip enterDelay={1000} placement="top" title="Results match selected filter values">
                <CheckBoxIcon className="results_match_filters" viewBox="0 0 24 24" />
              </Tooltip>
            )}
          </Grid>
        </div>
      )}
    </OCEventFilter>
  );
};
